import { Editor, Element as SlateElement, Node, Transforms } from 'slate';
import vCard from 'vcards-js';
import { hasAttachment, hasImage } from './with-document-structure';
import { removeImage } from './with-images';

/**
 * Types for contact information.
 * We use separate types for phone numbers and emails to:
 * 1. Provide better type safety and validation
 * 2. Make the intent clearer (a phone isn't just a string)
 * 3. Support future extensions (formatting, validation, etc.)
 */

export type PhoneType = 'CELL' | 'WORK' | 'HOME';
export type EmailType = 'WORK' | 'PERSONAL';

export interface PhoneNumber {
  type: PhoneType;
  number: string;
}

export interface Email {
  type: EmailType;
  address: string;
}

/**
 * Core data structure for contact cards.
 * Includes all necessary information for:
 * 1. Display in the editor
 * 2. vCard generation
 * 3. Contact management
 */
export interface ContactCardData {
  id: string;
  name: string;
  phoneNumbers: PhoneNumber[];
  emails: Email[];
  image?: string;
  vcfString?: string;
}

/**
 * Slate element type for contact cards.
 * Follows Slate's pattern of:
 * 1. Specific type identifier
 * 2. Custom data payload
 * 3. Required children array (empty for void nodes)
 */
export interface ContactCardElement {
  type: 'contact-card';
  data: ContactCardData;
  children: [{ text: '' }];
}

// Type guard for contact card elements
function _isContactCard(n: Node): n is ContactCardElement {
  return SlateElement.isElement(n) && n.type === 'contact-card';
}

/**
 * Generates a vCard string using vcards-js.
 * We generate this at creation/edit time to:
 * 1. Validate the data immediately
 * 2. Ensure the preview matches the final output
 * 3. Simplify the sending process
 */
export function generateVcfString(data: Omit<ContactCardData, 'id' | 'vcfString'>) {
  const vcard = vCard();

  // Set basic info
  const [firstName, ...lastNameParts] = data.name.split(' ');
  vcard.firstName = firstName;
  vcard.lastName = lastNameParts.join(' ');

  // Add phone numbers
  data.phoneNumbers.forEach((phone) => {
    switch (phone.type) {
      case 'CELL':
        vcard.cellPhone = phone.number;
        break;
      case 'WORK':
        vcard.workPhone = phone.number;
        break;
      case 'HOME':
        vcard.homePhone = phone.number;
        break;
    }
  });

  // Add emails
  data.emails.forEach((email) => {
    if (email.type === 'WORK') {
      vcard.workEmail = email.address;
    } else {
      vcard.email = email.address; // Personal email
    }
  });

  // Add photo if present
  if (data.image) {
    vcard.photo.embedFromString(data.image, 'image/jpeg');
  }

  return vcard.getFormattedString();
}

/**
 * Creates a new contact card node.
 * Follows Slate's pattern for void nodes with:
 * 1. Specific type identifier
 * 2. Custom data
 * 3. Empty children array
 */
export function createContactCardNode(data: ContactCardData): ContactCardElement {
  return {
    type: 'contact-card',
    data,
    children: [{ text: '' }]
  };
}

/**
 * Inserts a contact card at the current selection.
 * Uses Slate's transform system for proper history handling.
 */
export function insertContactCard(editor: Editor, data: ContactCardData) {
  // If there's already an attachment, remove it first
  if (hasAttachment(editor)) {
    // Force normalization to ensure we have latest state
    Editor.normalize(editor, { force: true });

    // Handle image replacement explicitly
    if (hasImage(editor)) {
      const nodeEntry = Editor.nodes(editor, {
        at: [],
        match: (n) => SlateElement.isElement(n) && n.type === 'image'
      }).next();

      if (!nodeEntry.done) {
        const [node] = nodeEntry.value;
        removeImage(editor, node);
      }
    } else {
      // Handle contact card replacement
      const nodeEntry = Editor.nodes(editor, {
        at: [],
        match: (n) => SlateElement.isElement(n) && n.type === 'contact-card'
      }).next();

      if (!nodeEntry.done) {
        const [node] = nodeEntry.value;
        removeContactCard(editor, node);
      }
    }
  }

  const node = createContactCardNode(data);
  Transforms.insertNodes(editor, node);
}

// In-memory storage for contact cards (temporary solution)
export const contactCardStore = new Map<string, ContactCardData>();

/**
 * Plugin to enable contact card handling in the Slate editor.
 *
 * Key features:
 * 1. Marks contact cards as void nodes (no editing inside)
 * 2. Works with withDocumentStructure for MMS attachment rules
 * 3. Maintains clean separation between editor behavior and data management
 */
export function withContactCards(editor: Editor) {
  const { isVoid } = editor;

  editor.isVoid = (element) => {
    return element.type === 'contact-card' ? true : isVoid(element);
  };

  return editor;
}

/**
 * Removes a contact card element from the editor.
 * Uses fresh node references and handles race conditions with document structure plugin.
 *
 * @param editor - The Slate editor instance
 * @param element - The contact card element to remove
 * @returns boolean indicating if removal was successful
 */
export function removeContactCard(editor: Editor, element: ContactCardElement): boolean {
  try {
    // Force normalization first to let document structure settle
    Editor.normalize(editor, { force: true });

    // Get fresh reference to ensure we have the latest state
    const nodeEntry = Editor.nodes(editor, {
      at: [],
      match: (n) =>
        SlateElement.isElement(n) &&
        // Match by id since that uniquely identifies a contact card
        'data' in n &&
        n.data.id === element.data.id
    }).next().value;

    if (nodeEntry) {
      const [_, path] = nodeEntry;
      Transforms.removeNodes(editor, { at: path });
      return true;
    }

    return false;
  } catch (err) {
    console.debug('Contact card removal failed (path invalid or node missing)', err);
    return false;
  }
}
